import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Paper, Select,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import {
  Add as AddIcon, AssignmentTurnedIn as ReceiveIcon, FactCheck as InspectIcon,
  Search as SearchIcon, WarningAmber as WarningIcon
} from '@mui/icons-material';

const statuses = ['requested', 'approved', 'in_transit', 'received', 'inspected', 'resolution_pending', 'completed'];
const statusLabel = value => value?.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
const statusColor = value => ({ completed: 'success', received: 'info', inspected: 'secondary', resolution_pending: 'warning', in_transit: 'primary' }[value] || 'default');

const OperationsWorkspace = () => {
  const [summary, setSummary] = useState(null);
  const [amazonSync, setAmazonSync] = useState(null);
  const [queue, setQueue] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState('receive');
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [workflow, setWorkflow] = useState({ returnRef: '', platform: 'Sellercloud', status: 'requested', assignedTo: '', priority: 'normal', dueAt: '' });
  const [action, setAction] = useState({ trackingNumber: '', quantityExpected: 1, quantityReceived: 1, condition: 'new', disposition: 'restock', notes: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const exceptions = tab === 1 ? '?exceptions=true' : '';
      const [summaryResponse, queueResponse, amazonResponse] = await Promise.all([
        axios.get('/api/operations/summary'), axios.get(`/api/operations/queue${exceptions}`),
        axios.get('/api/amazon-sync/status')
      ]);
      setSummary(summaryResponse.data.data);
      setQueue(queueResponse.data.data);
      setAmazonSync(amazonResponse.data.data);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Unable to load the operations workspace');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveWorkflow = async () => {
    try {
      await axios.put(`/api/operations/${encodeURIComponent(workflow.returnRef)}/workflow`, {
        platform: workflow.platform, status: workflow.status, assignedTo: workflow.assignedTo || null,
        priority: workflow.priority, dueAt: workflow.dueAt || undefined
      });
      setWorkflowOpen(false);
      setMessage('Return added to the operational queue');
      await loadData();
    } catch (error) { setMessage(error.response?.data?.error || 'Unable to save workflow'); }
  };

  const openAction = (row, type) => {
    setSelected(row); setActionType(type); setActionOpen(true);
    setAction({ trackingNumber: '', quantityExpected: 1, quantityReceived: 1, condition: 'new', disposition: 'restock', notes: '' });
  };

  const submitAction = async () => {
    const endpoint = actionType === 'receive' ? 'receive' : 'inspect';
    try {
      await axios.post(`/api/operations/${encodeURIComponent(selected.return_ref)}/${endpoint}`, { ...action, platform: selected.platform });
      setActionOpen(false);
      setMessage(actionType === 'receive' ? 'Receiving event recorded' : 'Inspection recorded');
      await loadData();
    } catch (error) { setMessage(error.response?.data?.error || 'Unable to record action'); }
  };

  const runSearch = async () => {
    if (search.trim().length < 2) return;
    try {
      const response = await axios.get('/api/operations/search', { params: { q: search.trim() } });
      setSearchResults(response.data.data);
    } catch (error) { setMessage(error.response?.data?.error || 'Search failed'); }
  };

  const runAmazonSync = async () => {
    setMessage('Amazon sync started. The report can take several minutes to generate.');
    try {
      await axios.post('/api/amazon-sync/run', { type: 'incremental' });
      setMessage('Amazon returns are up to date');
      await loadData();
    } catch (error) { setMessage(error.response?.data?.error || 'Unable to sync Amazon returns'); }
  };

  const cards = [
    ['Active returns', summary?.active || 0, 'primary.main'],
    ['Open exceptions', summary?.openExceptions || 0, 'error.main'],
    ['SLA breaches', summary?.overdue || 0, 'warning.dark'],
    ['Received today', summary?.receivedToday || 0, 'success.main']
  ];

  return <Box sx={{ p: 3, bgcolor: '#f6f8fb', minHeight: 'calc(100vh - 80px)' }}>
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
      <Box><Typography variant="h4" fontWeight={750} color="primary.dark">Returns Operations</Typography>
        <Typography color="text.secondary">Manage SLAs, ownership, receiving, inspection and exceptions.</Typography></Box>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWorkflowOpen(true)}>Add return to queue</Button>
    </Stack>

    {message && <Alert severity={message.includes('Unable') || message.includes('failed') ? 'error' : 'success'} onClose={() => setMessage('')} sx={{ mb: 2 }}>{message}</Alert>}
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
        <Box><Stack direction="row" spacing={1} alignItems="center"><Typography fontWeight={700}>Amazon returns cache</Typography>
          <Chip size="small" color={amazonSync?.latest?.status === 'failed' ? 'error' : 'success'} label={amazonSync?.running ? 'Syncing' : amazonSync?.latest?.status || 'Ready'} /></Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>
            {amazonSync?.lastSuccess?.finished_at ? `Last synced ${new Date(amazonSync.lastSuccess.finished_at).toLocaleString()} • ${amazonSync.cache?.total || 0} stored returns` : `${amazonSync?.cache?.total || 0} stored returns • No automated sync completed yet`}
          </Typography>
          {!amazonSync?.enabled && <Typography variant="caption" color="warning.dark">Automatic scheduling is off in this local V2 environment.</Typography>}
        </Box>
        <Button variant="outlined" disabled={amazonSync?.running} onClick={runAmazonSync}>Sync Amazon now</Button>
      </Stack>
    </Paper>
    <Grid container spacing={2} sx={{ mb: 3 }}>{cards.map(([label, value, colour]) =>
      <Grid key={label} size={{ xs: 12, sm: 6, lg: 3 }}><Card><CardContent><Typography color="text.secondary" variant="body2">{label}</Typography>
        <Typography variant="h3" fontWeight={750} sx={{ color: colour, mt: 1 }}>{value}</Typography></CardContent></Card></Grid>)}</Grid>

    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
        <TextField fullWidth size="small" label="Search RMA, order, tracking, SKU, customer or assignee" value={search}
          onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === 'Enter' && runSearch()} />
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={runSearch}>Search</Button>
      </Stack>
      {searchResults && <Box sx={{ mt: 2 }}><Typography variant="subtitle2" sx={{ mb: 1 }}>
        {Object.values(searchResults).flat().length} matching records</Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">{Object.values(searchResults).flat().map((result, index) =>
          <Chip key={`${result.platform}-${result.return_number}-${index}`} label={`${result.platform}: ${result.return_number}`} variant="outlined" />)}</Stack></Box>}
    </Paper>

    <Paper>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Operational queue" /><Tab icon={<WarningIcon />} iconPosition="start" label="Exceptions" />
      </Tabs>
      {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : queue.length === 0 ?
        <Box sx={{ p: 7, textAlign: 'center' }}><ReceiveIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>{tab === 1 ? 'No open exceptions' : 'The operational queue is empty'}</Typography>
          <Typography color="text.secondary">{tab === 1 ? 'Exceptions created during receiving and inspection appear here.' : 'Add a return to start tracking its workflow and SLA.'}</Typography></Box> :
        <TableContainer><Table size="small"><TableHead><TableRow sx={{ bgcolor: '#f1f5f9' }}>
          <TableCell>Return</TableCell><TableCell>Stage</TableCell><TableCell>Owner</TableCell><TableCell>Priority</TableCell><TableCell>Age / SLA</TableCell><TableCell>Exceptions</TableCell><TableCell align="right">Next action</TableCell>
        </TableRow></TableHead><TableBody>{queue.map(row => <TableRow key={row.return_ref} hover>
          <TableCell><Typography fontWeight={650}>{row.return_ref}</Typography><Typography variant="caption" color="text.secondary">{row.platform}</Typography></TableCell>
          <TableCell><Chip size="small" label={statusLabel(row.workflow_status)} color={statusColor(row.workflow_status)} /></TableCell>
          <TableCell>{row.assigned_to || 'Unassigned'}</TableCell><TableCell><Chip size="small" variant="outlined" label={statusLabel(row.priority)} /></TableCell>
          <TableCell><Typography variant="body2" color={row.sla_breached ? 'error.main' : 'text.primary'}>{row.age_days || 0} days {row.sla_breached ? '• Breached' : ''}</Typography></TableCell>
          <TableCell>{row.exception_count ? <Chip size="small" color="error" label={row.exception_count} /> : '—'}</TableCell>
          <TableCell align="right"><Button size="small" startIcon={<ReceiveIcon />} onClick={() => openAction(row, 'receive')}>Receive</Button>
            <Button size="small" startIcon={<InspectIcon />} onClick={() => openAction(row, 'inspect')}>Inspect</Button></TableCell>
        </TableRow>)}</TableBody></Table></TableContainer>}
    </Paper>

    <Dialog open={workflowOpen} onClose={() => setWorkflowOpen(false)} fullWidth maxWidth="sm"><DialogTitle>Add return to operational queue</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}><TextField required label="RMA or return reference" value={workflow.returnRef} onChange={e => setWorkflow({ ...workflow, returnRef: e.target.value })} />
        <TextField label="Platform" value={workflow.platform} onChange={e => setWorkflow({ ...workflow, platform: e.target.value })} />
        <FormControl><InputLabel>Stage</InputLabel><Select label="Stage" value={workflow.status} onChange={e => setWorkflow({ ...workflow, status: e.target.value })}>{statuses.map(status => <MenuItem key={status} value={status}>{statusLabel(status)}</MenuItem>)}</Select></FormControl>
        <TextField label="Assigned operator" value={workflow.assignedTo} onChange={e => setWorkflow({ ...workflow, assignedTo: e.target.value })} />
        <FormControl><InputLabel>Priority</InputLabel><Select label="Priority" value={workflow.priority} onChange={e => setWorkflow({ ...workflow, priority: e.target.value })}>{['low','normal','high','urgent'].map(value => <MenuItem key={value} value={value}>{statusLabel(value)}</MenuItem>)}</Select></FormControl>
        <TextField type="datetime-local" label="SLA due" InputLabelProps={{ shrink: true }} value={workflow.dueAt} onChange={e => setWorkflow({ ...workflow, dueAt: e.target.value })} /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setWorkflowOpen(false)}>Cancel</Button><Button variant="contained" disabled={!workflow.returnRef.trim()} onClick={saveWorkflow}>Add to queue</Button></DialogActions></Dialog>

    <Dialog open={actionOpen} onClose={() => setActionOpen(false)} fullWidth maxWidth="sm"><DialogTitle>{actionType === 'receive' ? 'Receive return' : 'Inspect return'} — {selected?.return_ref}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>{actionType === 'receive' ? <>
        <TextField label="Tracking number" value={action.trackingNumber} onChange={e => setAction({ ...action, trackingNumber: e.target.value })} />
        <Stack direction="row" spacing={2}><TextField fullWidth type="number" label="Expected quantity" value={action.quantityExpected} onChange={e => setAction({ ...action, quantityExpected: e.target.value })} />
          <TextField fullWidth type="number" label="Received quantity" value={action.quantityReceived} onChange={e => setAction({ ...action, quantityReceived: e.target.value })} /></Stack></> : <>
        <FormControl><InputLabel>Condition</InputLabel><Select label="Condition" value={action.condition} onChange={e => setAction({ ...action, condition: e.target.value })}>{['new','opened','used','damaged','incomplete'].map(value => <MenuItem key={value} value={value}>{statusLabel(value)}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Disposition</InputLabel><Select label="Disposition" value={action.disposition} onChange={e => setAction({ ...action, disposition: e.target.value })}>{['restock','refurbish','quarantine','destroy','supplier_return'].map(value => <MenuItem key={value} value={value}>{statusLabel(value)}</MenuItem>)}</Select></FormControl></>}
        <TextField multiline minRows={3} label="Notes" value={action.notes} onChange={e => setAction({ ...action, notes: e.target.value })} /></Stack></DialogContent>
      <DialogActions><Button onClick={() => setActionOpen(false)}>Cancel</Button><Button variant="contained" onClick={submitAction}>Record {actionType}</Button></DialogActions></Dialog>
  </Box>;
};

export default OperationsWorkspace;
